import React from 'react';
import { components, FilterOptionOption, NoticeProps } from 'react-select';
import { Button, Grid, ISelectGroup, ISelectOption, Select } from 'ui-toolkit';
import { ISpecificException, ReqExcSections } from '../services/RequirementsExceptionsService';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface IAddCustomReqExc {
  options: ISpecificException[];
  onDeleteClick: () => void;
  onCreateClick: (item: ISpecificException) => void;
  addCustomItem: (sectionName: ReqExcSections, text: string) => void;
  section: ReqExcSections;
}

interface IAddCustomReqExcState {
  customText: string | null;
  newItem: ISpecificException | null;
}

const NoOptionsMessageWithAddButton = (onAddButtonClick: () => void) => (props: NoticeProps<any>) => (
  <components.NoOptionsMessage {...props}>
    <Button intent="ghost" isInline onClick={onAddButtonClick} disabled={false}>
      + Add custom
    </Button>
  </components.NoOptionsMessage>
);

export class AddCustomReqExc extends React.Component<IAddCustomReqExc, IAddCustomReqExcState> {
  public static defaultProps: Partial<IAddCustomReqExc> = {};

  public readonly state: IAddCustomReqExcState = {
    customText: null,
    newItem: null,
  };

  private placeHolderText = `Start typing to add ${this.props.section === ReqExcSections.B1 ? 'a requirement' : 'an exception'} ...`;

  public render() {
    const options = this.getOptionsText();
    const NoOptionsMessage = NoOptionsMessageWithAddButton(() => this.addCustomItem());
    const onCustomItemChanged = (text: string) => {
      this.setState((state) => ({
        ...state,
        customText: text,
      }));
    };
    const onOptionSelected = (option: ISelectOption<ISpecificException> | null) => {
      if (!option) {
        this.props.onDeleteClick();
        return;
      }
      this.setState((state) => ({
        ...state,
        newItem: option.value,
      }));
      this.props.onCreateClick(option.value);
    };

    return (
      <Grid className="requirements-exceptions--row" templateColumns={'min-content 1fr min-content'}>
        <div />
        <div className="section-1-3">
          <Select
            isClearable
            options={options}
            placeholder={this.placeHolderText}
            openMenuOnClick={false}
            components={{ NoOptionsMessage }}
            filterOption={this.filterOptions}
            inputValue={this.state.customText ?? ''}
            onInputChange={onCustomItemChanged}
            classNamePrefix="react-select"
            onChange={onOptionSelected}
          />
        </div>
        <div className="requirements-exceptions--align-right">
          <Button intent="ghost" isInline onClick={this.props.onDeleteClick}>
            <FontAwesomeIcon icon={faTrash} color={'red'} />
          </Button>
        </div>
      </Grid>
    );
  }

  private addCustomItem() {
    if (this.state.customText) {
      this.props.addCustomItem(this.props.section, this.state.customText);
    }
  }

  private getOptionsText() {
    const groupMap = new Map<string, ISelectGroup<ISpecificException>>();
    this.props.options.forEach((item) => {
      const codeKey = item.code ?? 'Items without Code';
      const codeLabel = codeKey.replace(/_/g, ' ');
      const option: ISelectOption<ISpecificException> = { label: item.template, value: item };
      const group = groupMap.get(codeKey);
      if (!group) {
        groupMap.set(codeKey, {
          label: codeLabel,
          options: [option],
        });
      } else {
        group.options.push(option);
      }
    });
    return Array.from(groupMap.values());
  }

  private filterOptions = (
    option: FilterOptionOption<ISelectOption<ISpecificException>>,
    inputValue: string
  ): boolean => {
    const searchString = inputValue.toLowerCase();
    return (option.data.value.code?.toLowerCase() ?? '').includes(searchString);
  };
}
